import {MdFrameStick} from "../../model/frame/MdFrameBlock";

// the slope of a drawn stick piece, in its own along/cross coords: the cross offset
// of its Left (min-cross) face at the A end and at the B end, from the slot the
// layout gave the stick. 0 and 0 is a straight stick. See MdFrameStick.slope.
export type StickSlope = {a: number; b: number};

export const STRAIGHT: StickSlope = {a: 0, b: 0};

export function isStraight(s: StickSlope): boolean {
    return s.a === 0 && s.b === 0;
}

// the Left face's cross offset at `along` (past the ends, the slope goes on).
export function slopeAt(s: StickSlope, len: number, along: number): number {
    if(len <= 0) return s.a;
    return s.a + (s.b - s.a) * along / len;
}

// the cross extent of a band of stock thickness `t` laid along the slope: the stock
// is `t` thick square to the slope, so it takes more of the cross axis.
export function crossThickness(t: number, len: number, s: StickSlope): number {
    if(len <= 0) return t;
    const d = (s.b - s.a) / len;
    return t * Math.sqrt(1 + d * d);
}

// the slope's angle off the along axis, in degrees (0 for a straight stick).
export function slopeAngleDeg(s: StickSlope, len: number): number {
    if(len <= 0) return 0;
    return Math.atan(Math.abs(s.b - s.a) / len) * 180 / Math.PI;
}

// the slope of the part `piece` of a sloped stick that spans `span` along its edge,
// on `side` of the frame. The offsets point into the frame: positive on a leading
// side (top, left), negative on a trailing one. STRAIGHT for a stick with no slope.
export function pieceSlope(stick: MdFrameStick, side: "top" | "bottom" | "left" | "right", span: {min: number; max: number}, piece: {min: number; max: number}): StickSlope {
    const slope = stick.slope;
    const len = span.max - span.min;
    if(!slope || len <= 0) return STRAIGHT;

    const drop = slope.drop.get();
    const inward = side === "top" || side === "left" ? 1 : -1;
    const tall = slope.shortEnd === "B" ? span.min : span.max;
    const short = slope.shortEnd === "B" ? span.max : span.min;
    const at = (x: number) => inward * drop * (x - tall) / (short - tall);
    return {a: at(piece.min), b: at(piece.max)};
}

// how deep into the frame a sloped stick's inner face lies at `along` on its edge
// (from the frame outline), given the slot thickness the layout gave the stick.
export function slopeDepthAt(stick: MdFrameStick, side: "top" | "bottom" | "left" | "right", span: {min: number; max: number}, t: number, along: number): number {
    const s = pieceSlope(stick, side, span, span);
    return Math.abs(slopeAt(s, span.max - span.min, along - span.min)) + crossThickness(t, span.max - span.min, s);
}
